import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from '../store'
import { fetchCurrentUser } from '../slices/authSlice'
import { canRedirectToLogin } from '../lib/apiFetch'
import { login } from './UserMenu'

interface RequireAuthProps {
  children: React.ReactNode
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { user, loading, checked, authError } = useSelector((state: RootState) => state.auth)
  // Set once the shared login-redirect budget is spent: stop navigating automatically and fall back
  // to the in-page Sign in button, matching the contract in useSignInOnUnauthorized.
  const [redirectThrottled, setRedirectThrottled] = useState(false)

  useEffect(() => {
    if (!checked) {
      dispatch(fetchCurrentUser())
    }
  }, [dispatch, checked])

  // Auto-bounce a signed-out visitor through login, but through the SAME redirect budget every other
  // automatic login path uses (canRedirectToLogin). A login that keeps coming back still
  // unauthenticated — a broken session, or /api/auth-me serving a stale signed-out identity — must
  // not turn this route into a full-page reload loop: QA bug bug-muhclfi9-j3gm recorded /projects
  // booting the entire app 32 times in ~70s (each boot re-downloading every hashed asset from the
  // edge, e.g. index-DxGFCBNo.css 64 times) because this effect re-navigated after every bounce.
  useEffect(() => {
    if (checked && !loading && !user && !authError) {
      if (canRedirectToLogin()) {
        login(dispatch, window.location.pathname + window.location.search)
      } else {
        setRedirectThrottled(true)
      }
    }
  }, [dispatch, checked, loading, user, authError])

  if (!checked || loading) {
    return null
  }

  if (authError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Authentication service is temporarily unavailable. Please try refreshing the page.</p>
      </div>
    )
  }

  if (!user) {
    if (!redirectThrottled) return null
    // The visitor was already bounced to login and came back still unauthenticated enough times to
    // exhaust the budget. Render a calm fallback with a manual Sign in button instead of another
    // automatic redirect — a user gesture may still navigate; the loop may not.
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Sign in to view this page.</p>
        <button
          type="button"
          data-testid="require-auth-signin"
          style={{ marginTop: '0.75rem', padding: '0.5rem 1.25rem', cursor: 'pointer' }}
          onClick={() => login(dispatch, window.location.pathname + window.location.search)}
        >
          Sign in
        </button>
      </div>
    )
  }

  return <>{children}</>
}
